import { formatCurrency, formatQuantity, formatRate, formatPercentage } from '@/utils/formatters';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import DashboardLayout from '../components/DashboardLayout';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { toast } from 'sonner';
import { Shield, Calendar, MapPin, User, Clock, FileText, RefreshCw, Search } from 'lucide-react';

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function SecurityCheckinDashboard() {
  const [loading, setLoading] = useState(true);
  const [checkins, setCheckins] = useState([]);
  const [locations, setLocations] = useState([]);
  const [securityStaff, setSecurityStaff] = useState([]);
  
  const [filters, setFilters] = useState({
    date_from: new Date().toISOString().split('T')[0],
    date_to: new Date().toISOString().split('T')[0],
    location_id: '',
    security_person_id: ''
  });

  const token = localStorage.getItem('token');

  // Load initial data
  useEffect(() => {
    loadData();
    loadLocations();
    loadSecurityStaff();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (filters.date_from) params.append('date_from', filters.date_from + 'T00:00:00Z');
      if (filters.date_to) params.append('date_to', filters.date_to + 'T23:59:59Z');
      if (filters.location_id) params.append('location_id', filters.location_id);
      if (filters.security_person_id) params.append('security_person_id', filters.security_person_id);
      
      const response = await axios.get(`${API_URL}/store/security-checkins?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCheckins(response.data);
    } catch (error) {
      console.error('Error loading check-ins:', error);
      toast.error('Failed to load check-ins');
    } finally {
      setLoading(false);
    }
  };

  const loadLocations = async () => {
    try {
      const response = await axios.get(`${API_URL}/locations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLocations(response.data);
    } catch (error) {
      console.error('Error loading locations:', error);
    }
  };

  const loadSecurityStaff = async () => {
    try {
      const response = await axios.get(`${API_URL}/hr-employees`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      // Filter for security department
      const security = response.data.filter(emp => 
        emp.department?.toLowerCase() === 'security' && emp.status === 'Active'
      );
      setSecurityStaff(security);
    } catch (error) {
      console.error('Error loading security staff:', error);
    }
  };

  const handleFilter = () => {
    loadData();
  };

  const clearFilters = () => {
    setFilters({
      date_from: new Date().toISOString().split('T')[0],
      date_to: new Date().toISOString().split('T')[0],
      location_id: '',
      security_person_id: ''
    });
  };

  const formatDateTime = (isoString) => {
    if (!isoString) return '-';
    const date = new Date(isoString);
    return date.toLocaleString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusBadge = (status) => {
    if (status === 'active') {
      return <Badge className="bg-green-100 text-green-700">Active</Badge>;
    } else if (status === 'expired') {
      return <Badge className="bg-slate-100 text-slate-600">Expired</Badge>;
    }
    return <Badge variant="outline">{status}</Badge>;
  };

  // Summary stats
  const activeCount = checkins.filter(c => c.status === 'active').length;
  const expiredCount = checkins.filter(c => c.status === 'expired').length;
  const totalGateEntries = checkins.reduce((sum, c) => sum + (c.gate_entries_count || 0), 0);

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold flex items-center gap-2">
              <Shield className="w-6 h-6" />
              Security Check-ins
            </h1>
            <p className="text-muted-foreground">Monitor daily security personnel check-ins</p>
          </div>
          <Button onClick={loadData} variant="outline">
            <RefreshCw className="w-4 h-4 mr-2" />
            Refresh
          </Button>
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Total Check-ins</p>
                  <p className="text-3xl font-bold">{checkins.length}</p>
                </div>
                <User className="w-10 h-10 text-blue-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Active Sessions</p>
                  <p className="text-3xl font-bold text-green-600">{activeCount}</p>
                </div>
                <Clock className="w-10 h-10 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Expired Sessions</p>
                  <p className="text-3xl font-bold text-slate-500">{expiredCount}</p>
                </div>
                <Clock className="w-10 h-10 text-slate-400 opacity-50" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">Gate Entries Made</p>
                  <p className="text-3xl font-bold text-purple-600">{totalGateEntries}</p>
                </div>
                <FileText className="w-10 h-10 text-purple-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <Search className="w-5 h-5" />
              Filters
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
              <div className="space-y-2">
                <Label>From Date</Label>
                <Input
                  type="date"
                  value={filters.date_from}
                  onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>To Date</Label>
                <Input
                  type="date"
                  value={filters.date_to}
                  onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                />
              </div>
              
              <div className="space-y-2">
                <Label>Location</Label>
                <Select 
                  value={filters.location_id} 
                  onValueChange={(value) => setFilters({ ...filters, location_id: value === 'all' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Locations" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Locations</SelectItem>
                    {locations.map(loc => (
                      <SelectItem key={loc.id} value={loc.id}>{loc.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="space-y-2">
                <Label>Security Person</Label>
                <Select 
                  value={filters.security_person_id} 
                  onValueChange={(value) => setFilters({ ...filters, security_person_id: value === 'all' ? '' : value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="All Staff" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Staff</SelectItem>
                    {securityStaff.map(person => (
                      <SelectItem key={person.id} value={person.id}>{person.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-end gap-2">
                <Button onClick={handleFilter} className="flex-1">
                  Apply
                </Button>
                <Button onClick={clearFilters} variant="outline">
                  Clear
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Check-ins Table */}
        <Card>
          <CardHeader>
            <CardTitle>Check-in Records</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="text-center py-8">
                <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading...</p>
              </div>
            ) : checkins.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Shield className="w-12 h-12 mx-auto mb-4 opacity-30" />
                <p>No check-ins found for the selected filters</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Date</TableHead>
                      <TableHead>Security Person</TableHead>
                      <TableHead>Location</TableHead>
                      <TableHead>Check-in Time</TableHead>
                      <TableHead>Valid Until</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead className="text-right">Gate Entries</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {checkins.map((checkin) => {
                      const checkinDate = new Date(checkin.checked_in_at);
                      return (
                        <TableRow key={checkin.id}>
                          <TableCell>
                            {checkinDate.toLocaleDateString('en-IN', {
                              day: '2-digit',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </TableCell>
                          <TableCell className="font-medium">
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-slate-400" />
                              {checkin.security_person_name}
                            </div>
                          </TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <MapPin className="w-4 h-4 text-slate-400" />
                              {checkin.unit_location_name}
                            </div>
                          </TableCell>
                          <TableCell>
                            {checkinDate.toLocaleTimeString('en-IN', {
                              hour: '2-digit',
                              minute: '2-digit'
                            })}
                          </TableCell>
                          <TableCell>
                            {formatDateTime(checkin.valid_until)}
                          </TableCell>
                          <TableCell>
                            {getStatusBadge(checkin.status)}
                          </TableCell>
                          <TableCell className="text-right">
                            <Badge variant="outline" className="font-mono">
                              {checkin.gate_entries_count || 0}
                            </Badge>
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
